/**
 * Data Review Page
 * Dedicated page for reviewing transactions needing human attention
 * 
 * Features:
 * - Shows only transactions needing review (low confidence, flagged, anomalies)
 * - Batch approve/reject
 * - Inline category editing with AI learning
 * - Anomaly detection results
 * - Progress tracking toward 100% approval
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { 
  CheckCircle2, AlertTriangle, XCircle, Edit2, Check, X, 
  ChevronLeft, ChevronRight, Filter, Download, RefreshCw,
  Sparkles, TrendingUp, Clock, AlertOctagon
} from 'lucide-react';

// Categories for dropdown
const CATEGORIES = [
  "Revenue", "Sales Revenue", "Other Income",
  "Payroll & Benefits", "Payroll", "Labor",
  "Rent & Occupancy", "Rent", "Utilities", "Insurance",
  "COGS", "Food & Beverage Cost", "Inventory & Merchandise",
  "Variable Costs", "Marketing & Advertising", "Professional Services",
  "Technology & Software", "Office & Supplies", "Travel & Entertainment",
  "Bank & Financial Fees", "Taxes & Licenses", "Miscellaneous",
  "Donations Received", "Program Services", "Fundraising", "Administrative" // Non-profit
];

interface ReviewTransaction {
  id: string;
  transaction_date: string;
  description: string;
  amount: number;
  category: string;
  confidence: number;
  source_file?: string;
  anomaly?: {
    type: string;
    severity: string;
    reason: string;
    recommendation: string;
  };
  needs_review: boolean;
  validation_status: 'pending' | 'approved' | 'modified' | 'rejected';
}

interface ReviewStats {
  total: number;
  approved: number;
  needsReview: number;
  anomalies: number;
  lowConfidence: number;
}

export default function DataReviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  
  // State
  const [transactions, setTransactions] = useState<ReviewTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'needs_review' | 'anomalies' | 'low_confidence'>('needs_review');
  const [currentPage, setCurrentPage] = useState(1);
  const [runningAnomalyDetection, setRunningAnomalyDetection] = useState(false);
  
  const ITEMS_PER_PAGE = 50;
  const reportId = searchParams.get('reportId');
  
  // Load transactions from localStorage or API
  useEffect(() => {
    loadTransactions();
  }, [reportId]);
  
  const loadTransactions = async () => {
    setLoading(true);
    try {
      // First try to load from wizard state in localStorage
      const wizardData = localStorage.getItem('wizardData');
      if (wizardData) {
        const parsed = JSON.parse(wizardData);
        const unifiedData = parsed.unifiedUploadData?.canonical_data;
        
        if (unifiedData?.periods) {
          const allTxns: ReviewTransaction[] = [];
          
          unifiedData.periods.forEach((period: any, pIdx: number) => {
            (period.transactions || []).forEach((txn: any, tIdx: number) => {
              allTxns.push({
                id: `${pIdx}-${tIdx}`,
                transaction_date: txn.transaction_date,
                description: txn.description,
                amount: txn.amount,
                category: txn.category,
                confidence: txn.classification?.confidence || 0,
                source_file: txn.source_file,
                needs_review: txn.needs_review !== false && (txn.classification?.confidence || 0) < 0.95,
                validation_status: txn.validation_status || 'pending',
                anomaly: txn.anomaly
              });
            });
          });
          
          setTransactions(allTxns);
          setLoading(false);
          return;
        }
      }
      
      // Fallback to API if reportId provided
      if (reportId) {
        // Use centralized API utility
        const data = await api.get(`/api/reports/${reportId}/transactions`, { showErrorToast: false });
        setTransactions(data.transactions || []);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError('Failed to load transactions');
      setLoading(false);
    }
  };
  
  // Calculate stats
  const stats: ReviewStats = useMemo(() => {
    const approved = transactions.filter(t => t.validation_status === 'approved' || t.validation_status === 'modified').length;
    const needsReview = transactions.filter(t => t.needs_review && t.validation_status === 'pending').length;
    const anomalies = transactions.filter(t => t.anomaly).length;
    const lowConfidence = transactions.filter(t => t.confidence < 0.70).length;
    
    return {
      total: transactions.length,
      approved,
      needsReview,
      anomalies,
      lowConfidence
    };
  }, [transactions]);
  
  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      switch (filterType) {
        case 'needs_review':
          return t.needs_review && t.validation_status === 'pending';
        case 'anomalies':
          return t.anomaly !== undefined;
        case 'low_confidence':
          return t.confidence < 0.70;
        default:
          return true;
      }
    });
  }, [transactions, filterType]);
  
  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Approve single transaction
  const approveSingle = useCallback((id: string) => {
    setTransactions(prev => prev.map(t => 
      t.id === id ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
  }, []);
  
  // Bulk approve
  const approveSelected = useCallback(() => {
    setTransactions(prev => prev.map(t => 
      selectedIds.has(t.id) ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
    setSelectedIds(new Set());
  }, [selectedIds]);
  
  // Approve all visible
  const approveAllVisible = useCallback(() => {
    const visibleIds = new Set(paginatedTransactions.map(t => t.id));
    setTransactions(prev => prev.map(t => 
      visibleIds.has(t.id) && t.needs_review ? { ...t, validation_status: 'approved', needs_review: false } : t
    ));
  }, [paginatedTransactions]);
  
  // Edit category
  const startEdit = (txn: ReviewTransaction) => {
    setEditingId(txn.id);
    setEditCategory(txn.category);
  };
  
  const saveEdit = async (id: string) => {
    const txn = transactions.find(t => t.id === id);
    if (!txn) return;
    
    // Record AI learning
    if (txn.category !== editCategory) {
      try {
        // Use centralized API utility
        await api.post('/api/v1/cleanup/learnings/record', {
          description: txn.description,
          original_category: txn.category,
          corrected_category: editCategory,
          amount: txn.amount
        }, { showErrorToast: false });
        console.log('📚 AI Learning recorded');
      } catch (err) {
        console.error('Failed to record learning:', err);
      }
    }
    
    setTransactions(prev => prev.map(t => 
      t.id === id ? { 
        ...t, 
        category: editCategory, 
        validation_status: 'modified', 
        needs_review: false 
      } : t
    ));
    setEditingId(null);
  };
  
  // Run anomaly detection
  const runAnomalyDetection = async () => {
    setRunningAnomalyDetection(true);
    try {
      // Use centralized API utility
      const data = await api.post('/api/v1/anomalies/detect', {
        transactions: transactions.slice(0, 100).map(t => ({
          description: t.description,
          amount: t.amount,
          category: t.category,
          date: t.transaction_date
        }))
      });
      
      // Merge anomalies into transactions
      setTransactions(prev => prev.map((t, idx) => {
        const anomaly = data.anomalies?.find((a: any) => a.transaction_index === idx);
        return anomaly ? { ...t, anomaly, needs_review: true } : t;
      }));
    } catch (err) {
      console.error('Anomaly detection failed:', err);
    } finally {
      setRunningAnomalyDetection(false);
    }
  };
  
  // Format helpers
  const formatCurrency = (amount: number) => {
    const formatted = Math.abs(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount >= 0 ? `$${formatted}` : `-$${formatted}`;
  };
  
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.95) return 'bg-green-100 text-green-700';
    if (confidence >= 0.85) return 'bg-green-50 text-green-600';
    if (confidence >= 0.70) return 'bg-yellow-50 text-yellow-600';
    return 'bg-red-50 text-red-600';
  };
  
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };
  
  // Progress percentage
  const progressPercent = stats.total > 0 
    ? Math.round((stats.approved / stats.total) * 100) 
    : 0;
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </AppLayout>
    );
  }
  
  if (error) {
    return (
      <AppLayout>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Data Review</h1>
          <p className="text-gray-600">Review and approve transactions for your claim</p>
        </div>
        
        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Review Progress</span>
            <span className="text-sm font-bold text-blue-600">{progressPercent}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{stats.approved} approved</span>
            <span>{stats.needsReview} need review</span>
            <span>{stats.total} total</span>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
                <p className="text-sm text-gray-500">Approved</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.needsReview}</p>
                <p className="text-sm text-gray-500">Need Review</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertOctagon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.anomalies}</p>
                <p className="text-sm text-gray-500">Anomalies</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.lowConfidence}</p>
                <p className="text-sm text-gray-500">Low Confidence</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value as any); setCurrentPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Transactions ({transactions.length})</option>
                <option value="needs_review">Needs Review ({stats.needsReview})</option>
                <option value="anomalies">Anomalies ({stats.anomalies})</option>
                <option value="low_confidence">Low Confidence ({stats.lowConfidence})</option>
              </select>
            </div>
            
            {/* Selection info */}
            {selectedIds.size > 0 && (
              <span className="text-sm text-blue-600">
                {selectedIds.size} selected
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Anomaly detection button */}
            <button
              onClick={runAnomalyDetection}
              disabled={runningAnomalyDetection}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition disabled:opacity-50"
            >
              {runningAnomalyDetection ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Run AI Anomaly Check
            </button>
            
            {/* Bulk approve */}
            {selectedIds.size > 0 ? (
              <button
                onClick={approveSelected}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition"
              >
                <Check className="w-4 h-4" />
                Approve Selected ({selectedIds.size})
              </button>
            ) : (
              <button
                onClick={approveAllVisible}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition"
              >
                <Check className="w-4 h-4" />
                Approve All Visible
              </button>
            )}
          </div>
        </div>
        
        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === paginatedTransactions.filter(t => t.needs_review).length && selectedIds.size > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(paginatedTransactions.filter(t => t.needs_review).map(t => t.id)));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                    className="w-4 h-4 rounded"
                  />
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Description</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Category</th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">Confidence</th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">Status</th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedTransactions.map((txn) => (
                <tr 
                  key={txn.id}
                  className={`hover:bg-gray-50 ${txn.anomaly ? 'bg-red-50' : ''} ${txn.needs_review ? 'bg-yellow-50/50' : ''}`}
                >
                  {/* Checkbox */}
                  <td className="py-3 px-4">
                    {txn.needs_review && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(txn.id)}
                        onChange={() => {
                          const next = new Set(selectedIds);
                          if (next.has(txn.id)) next.delete(txn.id);
                          else next.add(txn.id);
                          setSelectedIds(next);
                        }}
                        className="w-4 h-4 rounded"
                      />
                    )}
                  </td>
                  
                  {/* Date */}
                  <td className="py-3 px-4 whitespace-nowrap text-gray-900">
                    {formatDate(txn.transaction_date)}
                  </td>
                  
                  {/* Description + Anomaly */}
                  <td className="py-3 px-4 max-w-xs">
                    <div className="truncate text-gray-800" title={txn.description}>
                      {txn.description}
                    </div>
                    {txn.anomaly && (
                      <div className={`mt-1 text-xs px-2 py-1 rounded border ${getSeverityColor(txn.anomaly.severity)}`}>
                        ⚠️ {txn.anomaly.reason}
                      </div>
                    )}
                  </td>
                  
                  {/* Amount */}
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <span className={txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(txn.amount)}
                    </span>
                  </td>
                  
                  {/* Category */}
                  <td className="py-3 px-4">
                    {editingId === txn.id ? (
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        autoFocus
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => startEdit(txn)}
                        className="px-2 py-1 bg-gray-100 hover:bg-blue-50 rounded text-xs text-gray-700 hover:text-blue-700 transition"
                      >
                        {txn.category}
                      </button>
                    )}
                  </td>
                  
                  {/* Confidence */}
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceColor(txn.confidence)}`}>
                      {Math.round(txn.confidence * 100)}%
                    </span>
                  </td>
                  
                  {/* Status */}
                  <td className="py-3 px-4 text-center">
                    {txn.validation_status === 'approved' || txn.validation_status === 'modified' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        <CheckCircle2 className="w-3 h-3" />
                        {txn.validation_status === 'modified' ? 'Modified' : 'Approved'}
                      </span>
                    ) : txn.needs_review ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        Review
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  
                  {/* Actions */}
                  <td className="py-3 px-4 text-center">
                    {editingId === txn.id ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => saveEdit(txn.id)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : txn.needs_review ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => approveSingle(txn.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(txn)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(txn)}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Empty state */}
          {paginatedTransactions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {filterType === 'needs_review' ? (
                <>
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-medium">All transactions reviewed!</p>
                  <p className="text-sm">Great job - no more items need attention.</p>
                </>
              ) : (
                <p>No transactions match the current filter.</p>
              )}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} • {filteredTransactions.length} transactions
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded border hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded border hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Bottom Actions */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => navigate('/wizard-v2')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to Wizard
          </button>
          
          {progressPercent >= 100 && (
            <button
              onClick={() => navigate('/wizard-v2?step=4')}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Continue to Date Selection →
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
